import { useState, useCallback, useRef, useEffect, Fragment } from 'react';
import { useParams, Link, useSearchParams, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeftIcon,
  MapPinIcon,
  ArrowUpTrayIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  PencilSquareIcon,
  PlusCircleIcon,
  XMarkIcon,
  CloudArrowUpIcon,
  DocumentIcon,
  DocumentTextIcon,
  FolderIcon,
  FolderOpenIcon,
  ArrowDownTrayIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ArrowPathIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';
import { apiClient } from '@/api/client';
import { sitesApi } from '@/api/sites';
import type { GISPreviewResponse, GISWellPreview } from '@/api/sites';
import type { MonitoringWell, Site } from '@/types';
import { useAuthStore } from '@/store/auth';
import {
  documentsApi,
  DOCUMENT_TYPE_LABELS,
  formatFileSize,
  type SiteDocument,
  type DocumentType,
} from '@/api/documents';
import { DocumentPreview } from '@/components/DocumentPreview';
import { ExtractionBadge } from '@/components/ExtractionStatusIcon';
import { concentrationApi } from '@/api/concentration';
import { ConcentrationSummaryCard } from '@/components/ConcentrationSummaryCard';
import { fetchRegulatoryStandards, type StandardLibrary } from '@/api/dataVault';
import { ClosureTimelineChart } from '@/components/ClosureTimelineChart';
import { WellPredictionsTable } from '@/components/WellPredictionsTable';
import { RouteErrorBoundary } from '@/components/RouteErrorBoundary';
import { AddWellModal } from '@/components/wells/AddWellModal';
import { WellConstructionPanel } from '@/pages/data-vault/components/WellConstructionPanel';
import {
  AQUIFER_OPTIONS,
  CONTAMINANT_OPTIONS,
  STATUS_OPTIONS,
  US_STATE_OPTIONS,
  type FieldDef,
  type FieldType,
} from '@/pages/site-fields';

type Tab = 'overview' | 'wells' | 'predictions' | 'documents' | 'receptors';

export function SiteDetail() {
  const { siteId } = useParams<{ siteId: string }>();
  const queryClient = useQueryClient();
  const { products } = useAuthStore();
  const hasPolygon = products.includes('polygon');
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const sitesPath = location.pathname.startsWith('/data-vault') ? '/data-vault/sites' : '/sites';
  const initialTab = (searchParams.get('tab') as Tab) || 'overview';
  const [activeTab, setActiveTab] = useState<Tab>(initialTab);

  const { data: site, isLoading } = useQuery({
    queryKey: ['site', siteId],
    queryFn: () => sitesApi.get(siteId!),
    enabled: !!siteId,
  });

  const { data: wells } = useQuery({
    queryKey: ['site-wells', siteId],
    queryFn: () => sitesApi.getWells(siteId!),
    enabled: !!siteId,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64 text-mono-500 text-xs tracking-widest">
        LOADING...
      </div>
    );
  }

  if (!site) {
    return (
      <div className="text-center py-16 text-mono-500 text-xs tracking-widest">
        SITE NOT FOUND
      </div>
    );
  }

  const tabs: { key: Tab; label: string; gated?: boolean }[] = [
    { key: 'overview', label: 'OVERVIEW' },
    { key: 'wells', label: `LOCATIONS (${wells?.length ?? 0})` },
    { key: 'predictions', label: 'PREDICTIONS', gated: !hasPolygon },
    { key: 'documents', label: 'DOCUMENTS' },
    { key: 'receptors', label: 'RECEPTORS' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4 border-b border-mono-800 pb-4">
        <Link to={sitesPath} className="text-mono-500 hover:text-mono-300">
          <ArrowLeftIcon className="h-4 w-4" />
        </Link>
        <div>
          <h1 className="text-lg font-medium text-mono-100 tracking-widest">
            {site.name}
          </h1>
          <p className="text-xs text-mono-500 tracking-wider">
            {site.site_identifier || ''} &middot; {site.city}, {site.state}
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-6 border-b border-mono-800">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => !tab.gated && setActiveTab(tab.key)}
            disabled={tab.gated}
            className={`pb-3 text-xs tracking-widest transition-colors ${
              tab.gated
                ? 'text-mono-700 cursor-not-allowed'
                : activeTab === tab.key
                  ? 'text-mono-100 border-b-2 border-mono-100'
                  : 'text-mono-500 hover:text-mono-300'
            }`}
            title={tab.gated ? 'Requires Polygon' : undefined}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content — wrapped so a render-time crash inside any tab shows
          a friendly fallback with reload/back buttons rather than blanking
          the page (which previously forced a re-login to recover). */}
      <RouteErrorBoundary routeName={`Site Detail · ${activeTab}`}>
        {activeTab === 'overview' && <OverviewTab site={site} siteId={siteId!} queryClient={queryClient} />}
        {activeTab === 'wells' && <WellsTab siteId={siteId!} wells={wells || []} queryClient={queryClient} />}
        {activeTab === 'predictions' && <PredictionsTab siteId={siteId!} />}
        {activeTab === 'documents' && <DocumentsTab siteId={siteId!} />}
        {activeTab === 'receptors' && <ReceptorsTab siteId={siteId!} />}
      </RouteErrorBoundary>
    </div>
  );
}

// Regulatory standard libraries are the canonical list from the backend
// (`/regulatory/standards` → regulatory_libraries table). The frontend
// no longer hardcodes this list — see hook in SiteDetail component below.
// Preset colors are still hardcoded since color is a display choice, not
// authoritative data.
const REG_STANDARD_DEFAULT_COLORS = [
  '#3b82f6', // blue — EPA
  '#f59e0b', // amber — TCEQ
  '#ef4444', // red — RRC 1
  '#f97316', // orange — RRC 2
  '#a855f7', // purple — RRC 3
  '#06b6d4', // cyan — state
  '#10b981', // emerald — tier 1
  '#84cc16', // lime — tier 2
];

function useRegulatoryStandardOptions() {
  const { data } = useQuery({
    queryKey: ['regulatory-standards'],
    queryFn: fetchRegulatoryStandards,
    staleTime: 5 * 60 * 1000,  // libraries rarely change
  });
  return (data ?? []).map((lib: StandardLibrary, i: number) => ({
    value: lib.library,
    label: lib.display_name || lib.library,
    color: REG_STANDARD_DEFAULT_COLORS[i % REG_STANDARD_DEFAULT_COLORS.length],
  }));
}

function EditableField({
  site,
  field,
  onSave,
  saving,
}: {
  site: Site;
  field: FieldDef;
  onSave: (key: string, value: unknown) => void;
  saving: string | null;
}) {
  const [editing, setEditing] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const raw = (site as any)[field.key];
  const display = raw != null && raw !== '' ? String(raw) : '--';
  const isSaving = saving === field.key;

  // Scale-aware view of the stored value. For µg/L → mg/L (scale=0.001) the
  // user sees and enters mg/L while the column keeps storing µg/L.
  const scale = field.displayScale ?? 1;
  const scaledDisplayValue =
    raw != null && raw !== '' && field.type === 'number' && scale !== 1
      ? Number(raw) * scale
      : raw;

  const formatDisplay = () => {
    if (raw == null || raw === '') return '--';
    if (field.type === 'number') {
      const n = Number(scaledDisplayValue);
      // Use raw string for high-precision fields (coordinates, gradient, porosity)
      const precise = field.step && parseFloat(field.step) < 0.01;
      const formatted = precise ? String(n) : n.toLocaleString();
      return field.unit ? `${formatted} ${field.unit}` : formatted;
    }
    if (field.type === 'select') {
      return field.options?.find((o) => o.value === String(raw))?.label ?? display;
    }
    return display;
  };

  if (field.readOnly) {
    return (
      <div className="flex items-center justify-between py-2.5 border-b border-mono-800/40">
        <span className="text-xs text-mono-500 tracking-wider shrink-0">{field.label}</span>
        <span className="text-sm text-mono-400 text-right">{formatDisplay()}</span>
      </div>
    );
  }

  // Boolean toggle
  if (field.type === 'boolean') {
    return (
      <div className="flex items-center justify-between py-2.5 border-b border-mono-800/40">
        <span className="text-xs text-mono-500 tracking-wider shrink-0">{field.label}</span>
        <button
          onClick={() => onSave(field.key, raw ? false : true)}
          className={`text-xs px-3 py-1 rounded border tracking-wider transition-colors ${
            raw
              ? 'border-green-700/60 bg-green-900/20 text-green-400'
              : 'border-mono-700 bg-mono-800/30 text-mono-500'
          } hover:border-mono-500`}
          disabled={isSaving}
        >
          {isSaving ? 'SAVING...' : raw ? 'YES' : 'NO'}
        </button>
      </div>
    );
  }

  // Select dropdown
  if (field.type === 'select' && editing) {
    return (
      <div className="flex items-center justify-between py-2.5 border-b border-mono-800/40">
        <span className="text-xs text-mono-500 tracking-wider shrink-0">{field.label}</span>
        <select
          autoFocus
          className="bg-mono-950 border border-cyan-500/50 rounded text-mono-200 text-sm px-3 py-1.5 focus:outline-none"
          defaultValue={raw != null ? String(raw) : ''}
          onBlur={(e) => {
            const val = e.target.value || null;
            setEditing(false);
            if (val !== (raw != null ? String(raw) : null)) onSave(field.key, val);
          }}
          onChange={(e) => {
            const val = e.target.value || null;
            setEditing(false);
            if (val !== (raw != null ? String(raw) : null)) onSave(field.key, val);
          }}
        >
          {field.options!.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>
    );
  }

  // Textarea
  if (field.type === 'textarea' && editing) {
    return (
      <div className="py-2.5 border-b border-mono-800/40">
        <span className="text-xs text-mono-500 tracking-wider block mb-2">{field.label}</span>
        <textarea
          autoFocus
          rows={4}
          className="w-full bg-mono-950 border border-cyan-500/50 rounded text-mono-200 text-sm px-3 py-2 focus:outline-none resize-y"
          defaultValue={raw != null ? String(raw) : ''}
          onBlur={(e) => {
            setEditing(false);
            const val = e.target.value || null;
            if (val !== (raw != null ? String(raw) : null)) onSave(field.key, val);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setEditing(false);
          }}
        />
      </div>
    );
  }

  // Inline input (text, number, date)
  if (editing) {
    return (
      <div className="flex items-center justify-between py-2.5 border-b border-mono-800/40">
        <span className="text-xs text-mono-500 tracking-wider shrink-0">{field.label}</span>
        <div className="flex items-center gap-2">
          <input
            autoFocus
            type={field.type === 'date' ? 'date' : 'text'}
            inputMode={field.type === 'number' ? 'decimal' : undefined}
            className="bg-mono-950 border border-cyan-500/50 rounded text-mono-200 text-sm px-3 py-1.5 focus:outline-none w-52"
            defaultValue={
              field.type === 'date' && raw
                ? String(raw).slice(0, 10)
                : scaledDisplayValue != null
                ? String(scaledDisplayValue)
                : ''
            }
            onBlur={(e) => {
              setEditing(false);
              let val: unknown = e.target.value || null;
              if (field.type === 'number' && val != null) {
                const n = parseFloat(val as string);
                val = isNaN(n) ? null : n / scale;
              }
              if (val !== raw) onSave(field.key, val);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
              if (e.key === 'Escape') setEditing(false);
            }}
          />
          {field.unit && <span className="text-xs text-mono-600">{field.unit}</span>}
        </div>
      </div>
    );
  }

  // Display mode
  return (
    <div className="py-2.5 border-b border-mono-800/40">
      <div className="flex items-center justify-between">
        <span className="text-xs text-mono-500 tracking-wider shrink-0">{field.label}</span>
        <button
          onClick={() => setEditing(true)}
          className="flex items-center gap-2 text-sm text-mono-200 hover:text-mono-100 transition-colors group"
          disabled={isSaving}
        >
          {isSaving ? (
            <span className="text-xs text-amber-400 tracking-wider">SAVING...</span>
          ) : (
            <>
              <span className={raw != null && raw !== '' ? '' : 'text-mono-600'}>
                {formatDisplay()}
              </span>
              <PencilSquareIcon className="h-3.5 w-3.5 text-mono-700 group-hover:text-mono-400 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
            </>
          )}
        </button>
      </div>
      {field.helpText && (
        <p className="mt-1 text-[10px] leading-snug text-mono-600">{field.helpText}</p>
      )}
    </div>
  );
}

// --- Collapsible section ---
function Section({ title, children, defaultOpen = true, accent = 'text-mono-300' }: { title: string; children: React.ReactNode; defaultOpen?: boolean; accent?: string }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-mono-900 border border-mono-800 rounded-lg overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-5 py-3 hover:bg-mono-800/40 transition-colors"
      >
        <h3 className={`text-xs font-semibold tracking-widest ${accent}`}>{title}</h3>
        <span className="text-mono-600 text-sm">{open ? '−' : '+'}</span>
      </button>
      {open && <div className="px-5 pb-4 border-t border-mono-800/50">{children}</div>}
    </div>
  );
}

// --- Regulatory Program Multi-Select ---
function RegulatoryProgramSelector({ siteId, clientId }: { siteId: string; clientId: string }) {
  const [configs, setConfigs] = useState<{ standard_library: string; is_primary: boolean }[]>([]);
  const [showPicker, setShowPicker] = useState(false);
  const [saving, setSaving] = useState(false);
  const regulatoryOptions = useRegulatoryStandardOptions();

  // Load current config
  useEffect(() => {
    apiClient.get(`/data-vault/sites/${siteId}/regulatory/config`)
      .then(({ data }) => {
        const items = (data.configs || data || []).map((c: any) => ({
          standard_library: c.standard_library,
          is_primary: c.is_primary || false,
        }));
        setConfigs(items);
      })
      .catch(() => setConfigs([]));
  }, [siteId]);

  const saveConfigs = async (newConfigs: typeof configs) => {
    setSaving(true);
    try {
      await apiClient.put(`/data-vault/sites/${siteId}/regulatory/config`, {
        configs: newConfigs.map((c, i) => ({
          standard_library: c.standard_library,
          display_name: regulatoryOptions.find(o => o.value === c.standard_library)?.label || c.standard_library,
          display_color: regulatoryOptions.find(o => o.value === c.standard_library)?.color || '#ef4444',
          display_style: 'dashed',
          is_primary: c.is_primary,
          sort_order: i,
          enabled: true,
        })),
      });
      setConfigs(newConfigs);
    } catch (e) {
      console.error('Failed to save regulatory config:', e);
    }
    setSaving(false);
  };

  const addProgram = (value: string) => {
    if (configs.some(c => c.standard_library === value)) return;
    const newConfigs = [...configs, { standard_library: value, is_primary: configs.length === 0 }];
    saveConfigs(newConfigs);
    setShowPicker(false);
  };

  const removeProgram = (value: string) => {
    let newConfigs = configs.filter(c => c.standard_library !== value);
    // If we removed the primary, make the first one primary
    if (newConfigs.length > 0 && !newConfigs.some(c => c.is_primary)) {
      newConfigs = [{ ...newConfigs[0], is_primary: true }, ...newConfigs.slice(1)];
    }
    saveConfigs(newConfigs);
  };

  const setPrimary = (value: string) => {
    const newConfigs = configs.map(c => ({
      ...c,
      is_primary: c.standard_library === value,
    }));
    saveConfigs(newConfigs);
  };

  const available = regulatoryOptions.filter(
    o => !configs.some(c => c.standard_library === o.value)
  );

  return (
    <div className="py-2">
      <div className="text-[10px] tracking-widest text-mono-500 mb-1.5">REGULATORY PROGRAMS</div>
      <div className="flex flex-wrap gap-1.5 min-h-[28px]">
        {configs.map(c => {
          const opt = regulatoryOptions.find(o => o.value === c.standard_library);
          return (
            <span
              key={c.standard_library}
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs border ${
                c.is_primary
                  ? 'border-cyan-500/50 bg-cyan-500/10 text-cyan-300'
                  : 'border-mono-700 bg-mono-800/40 text-mono-300'
              }`}
            >
              <button
                onClick={() => !c.is_primary && setPrimary(c.standard_library)}
                className={`text-[10px] ${c.is_primary ? 'text-cyan-400' : 'text-mono-500 hover:text-cyan-400 cursor-pointer'}`}
                title={c.is_primary ? 'Primary standard' : 'Set as primary'}
              >
                {c.is_primary ? '★' : '☆'}
              </button>
              {opt?.label || c.standard_library}
              <button
                onClick={() => removeProgram(c.standard_library)}
                className="text-mono-500 hover:text-red-400 ml-0.5"
                title="Remove"
              >
                ×
              </button>
            </span>
          );
        })}
        {configs.length === 0 && (
          <span className="text-mono-600 text-xs">No programs set</span>
        )}
        <button
          onClick={() => setShowPicker(!showPicker)}
          className="inline-flex items-center px-2 py-0.5 rounded text-xs border border-dashed border-mono-700 text-mono-500 hover:border-cyan-500/40 hover:text-cyan-400"
        >
          + Add
        </button>
      </div>
      {showPicker && available.length > 0 && (
        <div className="mt-1.5 flex flex-wrap gap-1">
          {available.map(o => (
            <button
              key={o.value}
              onClick={() => addProgram(o.value)}
              className="text-[11px] px-2 py-0.5 rounded border border-mono-700 text-mono-400 hover:border-amber-500/40 hover:text-amber-300"
            >
              {o.label}
            </button>
          ))}
        </div>
      )}
      {saving && <div className="text-[10px] text-mono-500 mt-1">Saving...</div>}
    </div>
  );
}

// --- Overview tab ---
function OverviewTab({
  site,
  siteId,
  queryClient,
}: {
  site: Site;
  siteId: string;
  queryClient: ReturnType<typeof useQueryClient>;
}) {
  const [saving, setSaving] = useState<string | null>(null);

  const updateMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => sitesApi.update(siteId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['site', siteId] });
      setSaving(null);
    },
    onError: () => setSaving(null),
  });

  const onSave = (key: string, value: unknown) => {
    setSaving(key);
    updateMutation.mutate({ [key]: value });
  };

  const f = (key: string, label: string, type: FieldType = 'text', extra?: Partial<FieldDef>): FieldDef => ({
    key, label, type, ...extra,
  });

  return (
    <div className="grid grid-cols-2 gap-5">
      {/* Left column */}
      <div className="space-y-5">
        <Section title="IDENTIFICATION" accent="text-cyan-400">
          <EditableField site={site} field={f('site_identifier', 'SITE ID', 'text', { readOnly: true })} onSave={onSave} saving={saving} />
          <EditableField site={site} field={f('name', 'NAME')} onSave={onSave} saving={saving} />
          <EditableField site={site} field={f('facility_id', 'FACILITY ID')} onSave={onSave} saving={saving} />
          <EditableField site={site} field={f('coordinate_source', 'COORD SOURCE', 'text', { readOnly: true })} onSave={onSave} saving={saving} />
          <EditableField site={site} field={f('source_system', 'SOURCE SYSTEM', 'text', { readOnly: true })} onSave={onSave} saving={saving} />
        </Section>

        <Section title="LOCATION" accent="text-emerald-400">
          <EditableField site={site} field={f('address', 'ADDRESS')} onSave={onSave} saving={saving} />
          <EditableField site={site} field={f('city', 'CITY')} onSave={onSave} saving={saving} />
          <EditableField site={site} field={f('state', 'STATE', 'select', { options: US_STATE_OPTIONS })} onSave={onSave} saving={saving} />
          <EditableField site={site} field={f('zip_code', 'ZIP CODE')} onSave={onSave} saving={saving} />
          <EditableField site={site} field={f('county', 'COUNTY')} onSave={onSave} saving={saving} />
          <EditableField site={site} field={f('latitude', 'LATITUDE', 'number', { step: '0.000001' })} onSave={onSave} saving={saving} />
          <EditableField site={site} field={f('longitude', 'LONGITUDE', 'number', { step: '0.000001' })} onSave={onSave} saving={saving} />
          <EditableField site={site} field={f('site_area_acres', 'SITE AREA', 'number', { unit: 'acres' })} onSave={onSave} saving={saving} />
          <EditableField site={site} field={f('land_use', 'LAND USE')} onSave={onSave} saving={saving} />
        </Section>

        <Section title="HYDROGEOLOGY" accent="text-blue-400">
          <EditableField site={site} field={f('aquifer_type', 'AQUIFER TYPE', 'select', { options: AQUIFER_OPTIONS })} onSave={onSave} saving={saving} />
          <EditableField site={site} field={f('depth_to_groundwater_ft', 'DEPTH TO GW', 'number', { unit: 'ft' })} onSave={onSave} saving={saving} />
          <EditableField site={site} field={f('hydraulic_conductivity', 'HYDRAULIC COND.', 'number', { unit: 'ft/day' })} onSave={onSave} saving={saving} />
          <EditableField site={site} field={f('hydraulic_gradient', 'HYDRAULIC GRAD.', 'number', { step: '0.000001' })} onSave={onSave} saving={saving} />
          <EditableField site={site} field={f('groundwater_flow_direction', 'FLOW DIRECTION', 'number', { unit: 'deg' })} onSave={onSave} saving={saving} />
          <EditableField site={site} field={f('porosity', 'POROSITY', 'number', { step: '0.0001' })} onSave={onSave} saving={saving} />
          <EditableField site={site} field={f('aquifer_thickness_ft', 'AQUIFER THICKNESS', 'number', { unit: 'ft' })} onSave={onSave} saving={saving} />
          <EditableField site={site} field={f('soil_type', 'SOIL TYPE')} onSave={onSave} saving={saving} />
        </Section>

        <Section title="SOURCE ZONE" accent="text-orange-400">
          <EditableField site={site} field={f('source_type', 'SOURCE TYPE')} onSave={onSave} saving={saving} />
          <EditableField site={site} field={f('spill_date', 'SPILL DATE', 'date')} onSave={onSave} saving={saving} />
          <EditableField site={site} field={f('estimated_release_volume_gal', 'RELEASE VOLUME', 'number', { unit: 'gal' })} onSave={onSave} saving={saving} />
          <EditableField site={site} field={f('source_latitude', 'SOURCE LAT', 'number', { step: '0.000001' })} onSave={onSave} saving={saving} />
          <EditableField site={site} field={f('source_longitude', 'SOURCE LON', 'number', { step: '0.000001' })} onSave={onSave} saving={saving} />
          <EditableField site={site} field={f('source_removed', 'SOURCE REMOVED', 'boolean')} onSave={onSave} saving={saving} />
        </Section>

        <Section title="CONTAMINATION" accent="text-red-400">
          <EditableField site={site} field={f('primary_contaminant', 'PRIMARY CONTAMINANT', 'select', { options: CONTAMINANT_OPTIONS })} onSave={onSave} saving={saving} />
          {/* Free-text companion — visible whenever 'Other' is the current
              selection, OR whenever the user has already filled it in
              (covers historic data where 'Other' got selected later). */}
          {(site.primary_contaminant === 'other' || site.primary_contaminant_other) && (
            <EditableField
              site={site}
              field={f('primary_contaminant_other', 'OTHER (SPECIFY)', 'text')}
              onSave={onSave}
              saving={saving}
            />
          )}
          <EditableField site={site} field={f('plume_area_sqft', 'PLUME AREA', 'number', { unit: 'sq ft' })} onSave={onSave} saving={saving} />
          <EditableField site={site} field={f('estimated_mass_lbs', 'ESTIMATED MASS', 'number', { unit: 'lbs' })} onSave={onSave} saving={saving} />
          {/* Column stored in µg/L; surface as mg/L per the ARGOS units policy. */}
          <EditableField site={site} field={f('max_concentration_ugl', 'MAX CONC.', 'number', { unit: 'mg/L', step: '0.0001', displayScale: 0.001 })} onSave={onSave} saving={saving} />
        </Section>
      </div>

      {/* Right column */}
      <div className="space-y-5">
        <Section title="STATUS & TIMELINE" accent="text-violet-400">
          <EditableField site={site} field={f('status', 'STATUS', 'select', { options: STATUS_OPTIONS })} onSave={onSave} saving={saving} />
          <EditableField site={site} field={f('discovery_date', 'DISCOVERY DATE', 'date')} onSave={onSave} saving={saving} />
          <EditableField site={site} field={f('remediation_start_date', 'REMEDIATION START', 'date')} onSave={onSave} saving={saving} />
          <EditableField site={site} field={f('estimated_closure_date', 'EST. CLOSURE', 'date')} onSave={onSave} saving={saving} />
          <EditableField site={site} field={f('actual_closure_date', 'ACTUAL CLOSURE', 'date')} onSave={onSave} saving={saving} />
        </Section>

        <Section title="REGULATORY" accent="text-amber-400">
          <RegulatoryProgramSelector siteId={site.id} clientId={site.client_id} />
          <EditableField site={site} field={f('case_number', 'CASE NUMBER')} onSave={onSave} saving={saving} />
          <EditableField site={site} field={f('responsible_party', 'RESPONSIBLE PARTY')} onSave={onSave} saving={saving} />
          <EditableField site={site} field={f('consulting_company', 'CONSULTING CO.')} onSave={onSave} saving={saving} />
        </Section>

        <Section title="FINANCIAL" accent="text-green-400">
          <EditableField site={site} field={f('total_cost_to_date', 'COST TO DATE', 'number', { unit: '$' })} onSave={onSave} saving={saving} />
          <EditableField site={site} field={f('annual_operating_cost', 'ANNUAL COST', 'number', { unit: '$' })} onSave={onSave} saving={saving} />
          <EditableField site={site} field={f('estimated_cost_to_closure', 'EST. COST TO CLOSE', 'number', { unit: '$' })} onSave={onSave} saving={saving} />
        </Section>

        <Section title="PSH / LNAPL" defaultOpen={false} accent="text-pink-400">
          <EditableField site={site} field={f('psh_present', 'PSH PRESENT', 'boolean')} onSave={onSave} saving={saving} />
          <EditableField site={site} field={f('psh_thickness_ft', 'MAX THICKNESS', 'number', { unit: 'ft' })} onSave={onSave} saving={saving} />
          <EditableField site={site} field={f('psh_area_sqft', 'PSH AREA', 'number', { unit: 'sq ft' })} onSave={onSave} saving={saving} />
          <EditableField site={site} field={f('psh_wells_count', 'WELLS W/ PRODUCT', 'number')} onSave={onSave} saving={saving} />
          <EditableField site={site} field={f('psh_recovery_active', 'RECOVERY ACTIVE', 'boolean')} onSave={onSave} saving={saving} />
          <EditableField site={site} field={f('psh_recovered_gallons', 'RECOVERED', 'number', { unit: 'gal' })} onSave={onSave} saving={saving} />
          <EditableField site={site} field={f('psh_source_type', 'PSH TYPE')} onSave={onSave} saving={saving} />
          <EditableField
            site={site}
            field={f('lnapl_specific_gravity', 'SPECIFIC GRAVITY', 'number', {
              step: '0.001',
              helpText: 'Default for all wells unless overridden at the well level. ARGOS default: 0.800.',
            })}
            onSave={onSave}
            saving={saving}
          />
        </Section>

        <Section title="NOTES" accent="text-mono-400">
          <EditableField site={site} field={f('notes', 'NOTES', 'textarea')} onSave={onSave} saving={saving} />
        </Section>
      </div>
    </div>
  );
}

function WellsTab({
  siteId,
  wells,
  queryClient,
}: {
  siteId: string;
  wells: MonitoringWell[];
  queryClient: ReturnType<typeof useQueryClient>;
}) {
  const [preview, setPreview] = useState<GISPreviewResponse | null>(null);
  const [excluded, setExcluded] = useState<Set<string>>(new Set());
  const [coordEdits, setCoordEdits] = useState<Record<string, { latitude: number; longitude: number }>>({});
  const [editingWell, setEditingWell] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [importResult, setImportResult] = useState<any>(null);
  const [addWellOpen, setAddWellOpen] = useState(false);
  const [openWellId, setOpenWellId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const GIS_EXTENSIONS = ['.shp', '.dbf', '.shx', '.prj', '.cpg', '.zip'];
  const XLSX_EXTENSIONS = ['.xlsx', '.xlsm'];
  const ACCEPTED_EXTENSIONS = [...GIS_EXTENSIONS, ...XLSX_EXTENSIONS];
  const [previewSource, setPreviewSource] = useState<'gis' | 'xlsx'>('gis');

  const handleFiles = useCallback(async (files: File[]) => {
    const getExt = (f: File) => f.name.substring(f.name.lastIndexOf('.')).toLowerCase();
    const xlsxFiles = files.filter((f) => XLSX_EXTENSIONS.includes(getExt(f)));
    const gisFiles = files.filter((f) => GIS_EXTENSIONS.includes(getExt(f)));

    // xlsx + gis in the same drop is ambiguous — make the user pick.
    if (xlsxFiles.length > 0 && gisFiles.length > 0) {
      setError('Drop either a wells xlsx OR shapefile pieces — not both at once.');
      return;
    }
    if (xlsxFiles.length === 0 && gisFiles.length === 0) {
      setError('Accepted: .xlsx (wells template) or .zip/.shp/.dbf/.shx/.prj/.cpg (shapefile).');
      return;
    }
    if (xlsxFiles.length > 1) {
      setError('Drop one wells xlsx at a time.');
      return;
    }

    setError(null);
    setUploading(true);
    setPreview(null);
    setImportResult(null);

    try {
      if (xlsxFiles.length === 1) {
        const result = await sitesApi.uploadWellsXLSX(siteId, xlsxFiles[0]);
        setPreview(result);
        setPreviewSource('xlsx');
      } else {
        const result = await sitesApi.uploadGIS(siteId, gisFiles);
        setPreview(result);
        setPreviewSource('gis');
      }
      setExcluded(new Set());
      setCoordEdits({});
      setEditingWell(null);
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  }, [siteId]);

  const onDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    handleFiles(files);
  }, [handleFiles]);

  const onDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const onDragLeave = useCallback(() => setDragOver(false), []);

  const confirmMutation = useMutation({
    mutationFn: () => {
      const fn = previewSource === 'xlsx' ? sitesApi.confirmWellsXLSX : sitesApi.confirmGIS;
      return fn(siteId, preview!.preview_id, Array.from(excluded), coordEdits);
    },
    onSuccess: (data) => {
      setImportResult(data);
      setPreview(null);
      queryClient.invalidateQueries({ queryKey: ['site-wells', siteId] });
    },
    onError: (e: any) => {
      setError(e?.response?.data?.detail || 'Import failed');
    },
  });

  const toggleExclude = (wellName: string) => {
    setExcluded((prev) => {
      const next = new Set(prev);
      if (next.has(wellName)) next.delete(wellName);
      else next.add(wellName);
      return next;
    });
  };

  return (
    <div className="space-y-6">
      {/* Action bar — single-well add (manual) or shapefile bulk-import below */}
      <div className="flex items-center justify-between">
        <p className="text-xs text-mono-500 tracking-wider">
          {wells.length} WELL{wells.length === 1 ? '' : 'S'} AT THIS SITE
        </p>
        <div className="flex items-center gap-3">
          <a
            href="/intake-templates/wells-template-v1.xlsx"
            download
            className="px-3 py-1.5 text-xs tracking-widest font-medium text-cyan-400 border border-cyan-500/40 rounded hover:bg-cyan-500/10 transition-colors"
          >
            DOWNLOAD TEMPLATE
          </a>
          <button
            type="button"
            onClick={() => setAddWellOpen(true)}
            className="px-3 py-1.5 text-xs tracking-widest font-medium text-cyan-400 border border-cyan-500/40 rounded hover:bg-cyan-500/10 transition-colors"
          >
            + ADD WELL
          </button>
        </div>
      </div>

      <AddWellModal
        siteId={siteId}
        open={addWellOpen}
        onClose={() => setAddWellOpen(false)}
      />

      {/* Drag and drop zone — accepts either a wells .xlsx (template) OR shapefile pieces / .zip */}
      <div
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onClick={() => fileInputRef.current?.click()}
        className={`border-2 border-dashed rounded cursor-pointer transition-colors p-8 text-center ${
          dragOver
            ? 'border-blue-400 bg-blue-400/5'
            : 'border-mono-700 hover:border-mono-500'
        }`}
      >
        <ArrowUpTrayIcon className="h-8 w-8 mx-auto text-mono-500 mb-3" />
        <p className="text-xs text-mono-300 tracking-wider">
          {uploading ? 'UPLOADING...' : 'DRAG & DROP .ZIP OR .XLSX HERE'}
        </p>
        <p className="text-[10px] text-mono-600 tracking-wider mt-1">
          .zip (.shp + .dbf + .shx + .prj [+ .cpg]) or .xlsx (wells template)
        </p>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".shp,.dbf,.shx,.prj,.cpg,.zip,.xlsx,.xlsm"
          className="hidden"
          onChange={(e) => {
            if (e.target.files) handleFiles(Array.from(e.target.files));
          }}
        />
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 p-3 border border-red-800 bg-red-900/20 text-red-300 text-xs">
          <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0" />
          {error}
          <button onClick={() => setError(null)} className="ml-auto">
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Import result */}
      {importResult && (
        <div className="flex items-center gap-2 p-3 border border-green-800 bg-green-900/20 text-green-300 text-xs">
          <CheckCircleIcon className="h-4 w-4 flex-shrink-0" />
          Import complete: {importResult.updated} updated, {importResult.inserted} new wells, {importResult.skipped_priority} skipped (higher priority)
          <button onClick={() => setImportResult(null)} className="ml-auto">
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Preview */}
      {preview && (
        <div className="border border-mono-800 space-y-4">
          <div className="flex items-center justify-between p-4 border-b border-mono-800 bg-mono-900">
            <div>
              <h3 className="text-xs text-mono-200 tracking-widest">PREVIEW: {preview.filename}</h3>
              <p className="text-[10px] text-mono-500 mt-1">
                {preview.total_features} wells &middot; {preview.matched} matched &middot;{' '}
                {preview.new_wells} new &middot; {preview.conflicts} conflicts
                {preview.crs && ` \u00b7 ${preview.crs}`}
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setPreview(null)}
                className="px-3 py-1.5 border border-mono-700 text-mono-400 text-[10px] tracking-widest hover:border-mono-500"
              >
                CANCEL
              </button>
              <button
                onClick={() => confirmMutation.mutate()}
                disabled={confirmMutation.isPending}
                className="px-3 py-1.5 border border-green-700 bg-green-900/30 text-green-300 text-[10px] tracking-widest hover:bg-green-900/50 disabled:opacity-50"
              >
                {confirmMutation.isPending ? 'APPLYING...' : `CONFIRM (${preview.total_features - excluded.size})`}
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-mono-800 bg-mono-900/50">
                  <th className="px-4 py-2 text-left text-[10px] text-mono-500 tracking-widest w-8"></th>
                  <th className="px-4 py-2 text-left text-[10px] text-mono-500 tracking-widest">WELL NAME</th>
                  <th className="px-4 py-2 text-left text-[10px] text-mono-500 tracking-widest">STATUS</th>
                  <th className="px-4 py-2 text-left text-[10px] text-mono-500 tracking-widest">GIS COORDS</th>
                  <th className="px-4 py-2 text-left text-[10px] text-mono-500 tracking-widest">EXISTING COORDS</th>
                  <th className="px-4 py-2 text-left text-[10px] text-mono-500 tracking-widest">ACTION</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-mono-800">
                {preview.wells.map((w: GISWellPreview) => {
                  const isExcluded = excluded.has(w.well_name);
                  return (
                    <tr
                      key={w.well_name}
                      className={`${isExcluded ? 'opacity-40' : ''} hover:bg-mono-900/30`}
                    >
                      <td className="px-4 py-2">
                        <input
                          type="checkbox"
                          checked={!isExcluded}
                          onChange={() => toggleExclude(w.well_name)}
                          className="accent-green-500"
                        />
                      </td>
                      <td className="px-4 py-2 text-xs text-mono-200">{w.well_name}</td>
                      <td className="px-4 py-2">
                        <StatusPill status={w.match_status} />
                      </td>
                      <td className="px-4 py-2 text-xs text-mono-400 font-mono">
                        {editingWell === w.well_name ? (
                          <div className="flex items-center gap-1">
                            <input
                              type="number"
                              step="0.000001"
                              className="w-24 bg-mono-900 border border-mono-600 text-mono-200 text-xs px-1.5 py-0.5 font-mono focus:border-blue-500 focus:outline-none"
                              defaultValue={(coordEdits[w.well_name]?.latitude ?? w.latitude).toFixed(6)}
                              onBlur={(e) => {
                                const val = parseFloat(e.target.value);
                                if (!isNaN(val)) {
                                  setCoordEdits((prev) => ({
                                    ...prev,
                                    [w.well_name]: {
                                      latitude: val,
                                      longitude: prev[w.well_name]?.longitude ?? w.longitude,
                                    },
                                  }));
                                }
                              }}
                            />
                            <span className="text-mono-600">,</span>
                            <input
                              type="number"
                              step="0.000001"
                              className="w-24 bg-mono-900 border border-mono-600 text-mono-200 text-xs px-1.5 py-0.5 font-mono focus:border-blue-500 focus:outline-none"
                              defaultValue={(coordEdits[w.well_name]?.longitude ?? w.longitude).toFixed(6)}
                              onBlur={(e) => {
                                const val = parseFloat(e.target.value);
                                if (!isNaN(val)) {
                                  setCoordEdits((prev) => ({
                                    ...prev,
                                    [w.well_name]: {
                                      latitude: prev[w.well_name]?.latitude ?? w.latitude,
                                      longitude: val,
                                    },
                                  }));
                                }
                              }}
                            />
                            <button
                              onClick={() => setEditingWell(null)}
                              className="text-mono-500 hover:text-mono-300 ml-1"
                              title="Done editing"
                            >
                              <CheckCircleIcon className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setEditingWell(w.well_name)}
                            className="flex items-center gap-1.5 hover:text-mono-200 transition-colors group"
                            title="Click to edit coordinates"
                          >
                            <span>
                              {(coordEdits[w.well_name]?.latitude ?? w.latitude).toFixed(6)},{' '}
                              {(coordEdits[w.well_name]?.longitude ?? w.longitude).toFixed(6)}
                            </span>
                            <PencilSquareIcon className="h-3 w-3 text-mono-600 group-hover:text-mono-400" />
                            {coordEdits[w.well_name] && (
                              <span className="text-[9px] text-amber-400 tracking-widest">EDITED</span>
                            )}
                          </button>
                        )}
                      </td>
                      <td className="px-4 py-2 text-xs text-mono-500 font-mono">
                        {w.existing_lat != null
                          ? `${w.existing_lat.toFixed(6)}, ${w.existing_lon!.toFixed(6)} (${w.existing_source})`
                          : '--'}
                      </td>
                      <td className="px-4 py-2 text-[10px] tracking-widest">
                        {w.match_status === 'new' && (
                          <span className="text-blue-400">CREATE</span>
                        )}
                        {w.match_status === 'matched' && w.will_update && (
                          <span className="text-green-400">UPDATE</span>
                        )}
                        {w.match_status === 'conflict' && w.will_update && (
                          <span className="text-amber-400">OVERWRITE</span>
                        )}
                        {!w.will_update && w.match_status !== 'new' && (
                          <span className="text-mono-600">SKIP (PRIORITY)</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Existing wells table — click a row to expand construction + survey history */}
      <div className="border border-mono-800">
        <div className="px-4 py-3 border-b border-mono-800 bg-mono-900">
          <h3 className="text-[10px] text-mono-500 tracking-widest">MONITORING WELLS</h3>
          <p className="text-[9px] text-mono-600 mt-0.5">Click a well to view / edit construction details and survey history.</p>
        </div>
        <table className="w-full">
          <thead>
            <tr className="border-b border-mono-800">
              <th className="px-4 py-2 text-left text-[10px] text-mono-500 tracking-widest w-6"></th>
              <th className="px-4 py-2 text-left text-[10px] text-mono-500 tracking-widest">WELL ID</th>
              <th className="px-4 py-2 text-left text-[10px] text-mono-500 tracking-widest">TYPE</th>
              <th className="px-4 py-2 text-left text-[10px] text-mono-500 tracking-widest">COORDINATES</th>
              <th className="px-4 py-2 text-left text-[10px] text-mono-500 tracking-widest">DEPTH (FT)</th>
              <th className="px-4 py-2 text-left text-[10px] text-mono-500 tracking-widest">STATUS</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-mono-800">
            {wells.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-mono-600 text-xs tracking-wider">
                  NO WELLS — UPLOAD A .ZIP OR .XLSX TO GET STARTED
                </td>
              </tr>
            ) : (
              wells.map((w: MonitoringWell) => {
                const isOpen = openWellId === w.id;
                return (
                  <Fragment key={w.id}>
                    <tr
                      className="hover:bg-mono-900/30 cursor-pointer"
                      onClick={() => setOpenWellId((prev) => (prev === w.id ? null : w.id))}
                    >
                      <td className="px-4 py-2 text-mono-600 text-[10px] select-none">{isOpen ? '▾' : '▸'}</td>
                      <td className="px-4 py-2 text-xs text-mono-200">{w.well_id}</td>
                      <td className="px-4 py-2 text-xs text-mono-400 uppercase">{w.well_type || '--'}</td>
                      <td className="px-4 py-2 text-xs text-mono-400 font-mono">
                        {w.latitude && w.longitude
                          ? `${Number(w.latitude).toFixed(6)}, ${Number(w.longitude).toFixed(6)}`
                          : '--'}
                      </td>
                      <td className="px-4 py-2 text-xs text-mono-400">
                        {w.total_depth_ft ?? '--'}
                      </td>
                      <td className="px-4 py-2">
                        <span className={`text-[10px] tracking-widest ${w.is_active ? 'text-green-400' : 'text-mono-600'}`}>
                          {w.is_active ? 'ACTIVE' : 'INACTIVE'}
                        </span>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr>
                        <td colSpan={6} className="px-4 py-3 bg-mono-950/40">
                          <WellConstructionPanel
                            wellId={w.id}
                            onClose={() => setOpenWellId(null)}
                            onElevationHistoryChanged={() =>
                              queryClient.invalidateQueries({ queryKey: ['site-wells', siteId] })
                            }
                          />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// --- Document type icons & colors ---
const DOC_TYPE_ICONS: Record<DocumentType, typeof DocumentIcon> = {
  agmr: DocumentTextIcon,
  gw_gradient: MapPinIcon,
  gw_concentrations: MapPinIcon,
  om: DocumentIcon,
  product_recovery: DocumentIcon,
  lab_report: DocumentTextIcon,
  other: FolderIcon,
};

const DOC_TYPE_COLORS: Record<DocumentType, string> = {
  agmr: 'text-cyan-400 bg-cyan-500/20',
  gw_gradient: 'text-purple-400 bg-purple-500/20',
  gw_concentrations: 'text-green-400 bg-green-500/20',
  om: 'text-orange-400 bg-orange-500/20',
  product_recovery: 'text-yellow-400 bg-yellow-500/20',
  lab_report: 'text-blue-400 bg-blue-500/20',
  other: 'text-mono-400 bg-mono-500/20',
};

const SUPPORTED_ACCEPT = '.pdf,.xlsx,.xls,.csv,.png,.jpg,.jpeg,.gif,.tiff,.tif,.doc,.docx,.txt,.shp,.dbf,.shx,.prj,.zip';

const ALL_SUPPORTED_EXTENSIONS = [
  '.pdf', '.doc', '.docx', '.txt',
  '.xlsx', '.xls', '.csv',
  '.png', '.jpg', '.jpeg', '.gif', '.tiff', '.tif',
  '.shp', '.dbf', '.shx', '.prj', '.zip',
];

// --- Documents tab ---
function DocumentsTab({ siteId }: { siteId: string }) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [documents, setDocuments] = useState<SiteDocument[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filterType, setFilterType] = useState<DocumentType | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  // Upload state
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadDocType, setUploadDocType] = useState<DocumentType | 'auto'>('auto');
  const [uploadDate, setUploadDate] = useState('');
  const [uploadDescription, setUploadDescription] = useState('');
  const [uploadProgress, setUploadProgress] = useState<{ current: number; total: number } | null>(null);

  // Delete state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [docToDelete, setDocToDelete] = useState<SiteDocument | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Drag and drop
  const [isDragging, setIsDragging] = useState(false);

  // Preview
  const [previewDocument, setPreviewDocument] = useState<SiteDocument | null>(null);

  // Open document in a new browser tab
  const openInNewTab = (doc: SiteDocument) => {
    const url = documentsApi.getDirectViewUrl(siteId, doc.id);
    window.open(url, '_blank');
  };

  // Load documents
  const loadDocuments = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await documentsApi.list(siteId, {
        document_type: filterType === 'all' ? undefined : filterType,
        page_size: 100,
      });
      setDocuments(response.items);
    } catch {
      setError('Failed to load documents');
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  }, [siteId, filterType]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  // Filter documents
  const filteredDocuments = documents.filter((doc) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      doc.original_filename.toLowerCase().includes(q) ||
      doc.description?.toLowerCase().includes(q) ||
      DOCUMENT_TYPE_LABELS[doc.document_type].toLowerCase().includes(q)
    );
  });

  // Stats by type
  const docsByType = documents.reduce((acc, doc) => {
    acc[doc.document_type] = (acc[doc.document_type] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  // Upload handler
  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;
    setIsUploading(true);
    setUploadError(null);
    setUploadProgress({ current: 0, total: selectedFiles.length });

    let successCount = 0;
    try {
      for (let i = 0; i < selectedFiles.length; i++) {
        setUploadProgress({ current: i + 1, total: selectedFiles.length });
        const result = await documentsApi.upload(
          siteId,
          selectedFiles[i],
          uploadDocType,
          uploadDate || undefined,
          uploadDescription || undefined,
        );
        if (result.success) successCount++;
      }
      const failCount = selectedFiles.length - successCount;
      setSuccessMsg(
        failCount === 0
          ? `Uploaded ${successCount} document(s)`
          : `Uploaded ${successCount}, ${failCount} failed`,
      );
      setShowUploadModal(false);
      setSelectedFiles([]);
      setUploadDocType('auto');
      setUploadDate('');
      setUploadDescription('');
      loadDocuments();
    } catch {
      setUploadError('Upload failed');
    } finally {
      setIsUploading(false);
      setUploadProgress(null);
    }
  };

  // File selection
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      setSelectedFiles(Array.from(files));
      setShowUploadModal(true);
    }
    e.target.value = '';
  };

  // Drag and drop
  const handleDragOver = (e: React.DragEvent) => { e.preventDefault(); setIsDragging(true); };
  const handleDragLeave = (e: React.DragEvent) => { e.preventDefault(); setIsDragging(false); };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (!files || files.length === 0) return;
    const valid: File[] = [];
    for (let i = 0; i < files.length; i++) {
      const ext = '.' + files[i].name.toLowerCase().split('.').pop();
      if (ALL_SUPPORTED_EXTENSIONS.includes(ext)) valid.push(files[i]);
    }
    if (valid.length > 0) {
      setSelectedFiles(valid);
      setShowUploadModal(true);
    } else {
      setError('No supported file types in drop');
    }
  };

  // Download
  const handleDownload = async (doc: SiteDocument) => {
    try {
      await documentsApi.download(siteId, doc.id, doc.original_filename);
    } catch {
      setError('Download failed');
    }
  };

  // Delete
  const handleConfirmDelete = async () => {
    if (!docToDelete) return;
    setDeleting(true);
    try {
      await documentsApi.delete(siteId, docToDelete.id);
      loadDocuments();
      setShowDeleteModal(false);
      setDocToDelete(null);
    } catch {
      setError('Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-5">
      {/* Header with upload button */}
      <div className="flex items-center justify-between">
        <h2 className="text-xs text-mono-500 tracking-widest">DOCUMENTS</h2>
        <button
          onClick={() => fileInputRef.current?.click()}
          className="flex items-center px-4 py-2 bg-cyan-500/20 border border-cyan-500/50 text-cyan-400 text-xs tracking-widest hover:bg-cyan-500/30 transition-colors"
        >
          <CloudArrowUpIcon className="h-4 w-4 mr-2" />
          UPLOAD
        </button>
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        accept={SUPPORTED_ACCEPT}
        multiple
        onChange={handleFileSelect}
      />

      {/* Success / Error banners */}
      {successMsg && (
        <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-3 flex items-center">
          <CheckCircleIcon className="h-4 w-4 text-green-400 mr-2" />
          <span className="text-green-400 text-sm">{successMsg}</span>
          <button onClick={() => setSuccessMsg(null)} className="ml-auto text-green-400 hover:text-green-300">&times;</button>
        </div>
      )}
      {error && (
        <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-3 flex items-center">
          <ExclamationTriangleIcon className="h-4 w-4 text-red-400 mr-2" />
          <span className="text-red-400 text-sm">{error}</span>
          <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-300">&times;</button>
        </div>
      )}

      {/* Drop zone */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
          isDragging ? 'border-cyan-500 bg-cyan-500/10' : 'border-mono-700 hover:border-mono-600'
        }`}
      >
        <CloudArrowUpIcon className={`h-10 w-10 mx-auto mb-3 ${isDragging ? 'text-cyan-400' : 'text-mono-600'}`} />
        <p className="text-mono-400 text-sm">Drag and drop files here, or click UPLOAD above</p>
        <p className="text-mono-600 text-xs mt-2">PDF, Excel, CSV, Word, Images, GIS files (max 50MB)</p>
      </div>

      {/* Type category cards */}
      <div className="grid grid-cols-7 gap-3">
        {Object.entries(DOCUMENT_TYPE_LABELS).map(([type, label]) => {
          const count = docsByType[type] || 0;
          const Icon = DOC_TYPE_ICONS[type as DocumentType];
          const colors = DOC_TYPE_COLORS[type as DocumentType];
          return (
            <button
              key={type}
              onClick={() => setFilterType(filterType === type ? 'all' : (type as DocumentType))}
              className={`bg-mono-900 border rounded-lg p-3 text-left transition-colors ${
                filterType === type ? 'border-cyan-500/50' : 'border-mono-800 hover:border-mono-700'
              }`}
            >
              <div className={`inline-flex p-1.5 rounded ${colors} mb-2`}>
                <Icon className="h-4 w-4" />
              </div>
              <div className="text-xl font-bold text-mono-100">{count}</div>
              <div className="text-xs text-mono-400 whitespace-nowrap">{label}</div>
            </button>
          );
        })}
      </div>

      {/* Search and type filter */}
      <div className="flex items-center gap-4 bg-mono-900 border border-mono-800 rounded-lg p-4">
        <div className="flex items-center flex-1 bg-mono-950 rounded-lg px-3 py-2">
          <MagnifyingGlassIcon className="h-4 w-4 text-mono-500 mr-2" />
          <input
            type="text"
            placeholder="Search documents..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="bg-transparent text-mono-100 text-sm flex-1 outline-none placeholder:text-mono-600"
          />
        </div>
        <div className="flex items-center gap-2">
          <FunnelIcon className="h-4 w-4 text-mono-500" />
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value as DocumentType | 'all')}
            className="bg-mono-950 text-mono-100 text-sm rounded-lg px-3 py-2 border border-mono-800 outline-none"
          >
            <option value="all">All Types</option>
            {Object.entries(DOCUMENT_TYPE_LABELS).map(([type, label]) => (
              <option key={type} value={type}>{label}</option>
            ))}
          </select>
        </div>
        <button
          onClick={loadDocuments}
          className="p-2 text-mono-500 hover:text-mono-300 transition-colors"
          title="Refresh"
        >
          <ArrowPathIcon className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Document list */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <ArrowPathIcon className="h-8 w-8 text-cyan-500 animate-spin" />
        </div>
      ) : filteredDocuments.length === 0 ? (
        <div className="text-center py-12 bg-mono-900 border border-mono-800 rounded-lg">
          <FolderOpenIcon className="h-12 w-12 text-mono-600 mx-auto mb-4" />
          <p className="text-mono-500">No documents found</p>
          <p className="text-mono-600 text-sm mt-1">
            {filterType !== 'all' ? 'Try changing the filter or ' : ''}Upload your first document to get started
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {filteredDocuments.map((doc) => {
            const Icon = DOC_TYPE_ICONS[doc.document_type];
            const colors = DOC_TYPE_COLORS[doc.document_type];
            return (
              <div
                key={doc.id}
                onDoubleClick={() => openInNewTab(doc)}
                className="bg-mono-900 border border-mono-800 rounded-lg p-4 flex items-center gap-4 hover:border-mono-700 transition-colors cursor-pointer"
              >
                <div className={`p-2 rounded ${colors}`}>
                  <Icon className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-mono-100 font-medium truncate">{doc.original_filename}</span>
                    <span className={`text-xs px-2 py-0.5 rounded ${colors}`}>
                      {DOCUMENT_TYPE_LABELS[doc.document_type]}
                    </span>
                    {doc.extraction_status && (
                      <ExtractionBadge status={doc.extraction_status} onReview={() => {}} onRetry={() => {}} />
                    )}
                  </div>
                  <div className="flex items-center gap-4 text-xs text-mono-500 mt-1">
                    <span>{formatFileSize(doc.file_size)}</span>
                    {doc.document_date && (
                      <span className="text-cyan-400 font-medium">{doc.document_date.substring(0, 4)}</span>
                    )}
                    <span>Uploaded {new Date(doc.created_at).toLocaleDateString()}</span>
                  </div>
                  {doc.description && (
                    <p className="text-mono-500 text-xs mt-1 truncate">{doc.description}</p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => openInNewTab(doc)}
                    className="p-2 text-mono-500 hover:text-cyan-400 transition-colors"
                    title="Open in new tab"
                  >
                    <EyeIcon className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => handleDownload(doc)}
                    className="p-2 text-mono-500 hover:text-cyan-400 transition-colors"
                    title="Download"
                  >
                    <ArrowDownTrayIcon className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => { setDocToDelete(doc); setShowDeleteModal(true); }}
                    className="p-2 text-mono-500 hover:text-red-400 transition-colors"
                    title="Delete"
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Document preview modal */}
      {previewDocument && (
        <DocumentPreview
          document={previewDocument}
          siteId={siteId}
          onClose={() => setPreviewDocument(null)}
        />
      )}

      {/* Delete confirmation modal */}
      {showDeleteModal && docToDelete && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="bg-mono-900 border border-mono-700 rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-500/20 rounded-lg">
                <TrashIcon className="h-6 w-6 text-red-400" />
              </div>
              <h3 className="text-lg font-semibold text-mono-100">Delete Document</h3>
            </div>
            <p className="text-mono-400 text-sm mb-4">Are you sure you want to delete this document?</p>
            <div className="bg-mono-950 border border-mono-800 rounded-lg p-3 mb-4">
              <p className="text-mono-100 font-medium truncate">{docToDelete.original_filename}</p>
              <p className="text-mono-500 text-xs mt-1">{formatFileSize(docToDelete.file_size)}</p>
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => { setShowDeleteModal(false); setDocToDelete(null); }}
                disabled={deleting}
                className="px-4 py-2 text-mono-400 hover:text-mono-200 text-sm transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={deleting}
                className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload modal */}
      {showUploadModal && selectedFiles.length > 0 && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-mono-900 border border-mono-700 rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-mono-100 mb-4">
              {selectedFiles.length === 1 ? 'Upload Document' : `Upload ${selectedFiles.length} Documents`}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-mono-500 text-xs tracking-wider mb-1">
                  {selectedFiles.length === 1 ? 'FILE' : 'FILES'}
                </label>
                {selectedFiles.length === 1 ? (
                  <div className="bg-mono-950 rounded-lg p-3 text-mono-300 text-sm truncate">
                    {selectedFiles[0].name} ({formatFileSize(selectedFiles[0].size)})
                  </div>
                ) : (
                  <div className="bg-mono-950 rounded-lg p-3 text-mono-300 text-sm max-h-32 overflow-y-auto">
                    {selectedFiles.map((file, idx) => (
                      <div key={idx} className="truncate">{file.name} ({formatFileSize(file.size)})</div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-mono-500 text-xs tracking-wider mb-1">DOCUMENT TYPE</label>
                <select
                  value={uploadDocType}
                  onChange={(e) => setUploadDocType(e.target.value as DocumentType | 'auto')}
                  className="w-full bg-mono-950 text-mono-100 rounded-lg px-3 py-2 border border-mono-800 outline-none"
                >
                  <option value="auto">Auto-detect (AI)</option>
                  {Object.entries(DOCUMENT_TYPE_LABELS).map(([type, label]) => (
                    <option key={type} value={type}>{label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-mono-500 text-xs tracking-wider mb-1">DOCUMENT DATE (optional)</label>
                <input
                  type="date"
                  value={uploadDate}
                  onChange={(e) => setUploadDate(e.target.value)}
                  className="w-full bg-mono-950 text-mono-100 rounded-lg px-3 py-2 border border-mono-800 outline-none"
                />
              </div>

              <div>
                <label className="block text-mono-500 text-xs tracking-wider mb-1">DESCRIPTION (optional)</label>
                <textarea
                  value={uploadDescription}
                  onChange={(e) => setUploadDescription(e.target.value)}
                  rows={2}
                  className="w-full bg-mono-950 text-mono-100 rounded-lg px-3 py-2 border border-mono-800 outline-none resize-none"
                  placeholder="Brief description..."
                />
              </div>

              {uploadError && <div className="text-red-400 text-sm">{uploadError}</div>}
            </div>

            {uploadProgress && (
              <div className="mt-4">
                <div className="flex justify-between text-xs text-mono-400 mb-1">
                  <span>Uploading...</span>
                  <span>{uploadProgress.current} / {uploadProgress.total}</span>
                </div>
                <div className="w-full bg-mono-800 rounded-full h-2">
                  <div
                    className="bg-cyan-500 h-2 rounded-full transition-all"
                    style={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%` }}
                  />
                </div>
              </div>
            )}

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => { setShowUploadModal(false); setSelectedFiles([]); setUploadError(null); }}
                disabled={isUploading}
                className="px-4 py-2 text-mono-400 hover:text-mono-200 text-sm transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleUpload}
                disabled={isUploading}
                className="px-4 py-2 bg-cyan-500 text-mono-900 font-medium text-sm rounded hover:bg-cyan-400 transition-colors disabled:opacity-50"
              >
                {isUploading
                  ? `Uploading${selectedFiles.length > 1 ? ` (${uploadProgress?.current || 0}/${selectedFiles.length})` : ''}...`
                  : selectedFiles.length > 1
                  ? `Upload ${selectedFiles.length} Files`
                  : 'Upload'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PredictionsTab({ siteId }: { siteId: string }) {
  const [selectedAnalyte, setSelectedAnalyte] = useState<string | null>(null);

  const { data: predictions, isLoading: loadingPredictions } = useQuery({
    queryKey: ['concentration-predictions', siteId, selectedAnalyte],
    queryFn: () => concentrationApi.getWellPredictions(siteId, {
      analyte_code: selectedAnalyte || undefined,
    }),
    enabled: !!siteId,
  });

  const { data: summary, isLoading: loadingSummary } = useQuery({
    queryKey: ['concentration-summary', siteId],
    queryFn: () => concentrationApi.getClosureTimeline(siteId),
    enabled: !!siteId,
  });

  const isLoading = loadingPredictions || loadingSummary;

  if (isLoading && !predictions && !summary) {
    return (
      <div className="flex items-center justify-center h-64 text-mono-500 text-xs tracking-widest">
        <ArrowPathIcon className="h-4 w-4 animate-spin mr-2" />
        LOADING PREDICTIONS...
      </div>
    );
  }

  const hasPredictions = predictions && predictions.predictions.length > 0;
  const hasSummary = !!summary;

  if (!hasPredictions && !hasSummary) {
    return (
      <div className="text-center py-16 text-mono-500 text-xs tracking-widest">
        NO PREDICTIONS AVAILABLE
        <p className="text-mono-600 mt-2 text-[10px]">
          Run the concentration predictor to generate forecasts for this site.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Site Summary Card */}
      {hasSummary && (
        <ConcentrationSummaryCard summary={summary} isLoading={loadingSummary} />
      )}

      {/* Closure Timeline Chart */}
      {hasSummary && summary.closure_trajectory && summary.closure_trajectory.length > 0 && (
        <ClosureTimelineChart
          trajectory={summary.closure_trajectory}
          closureP10={summary.closure_p10_quarters}
          closureMedian={summary.closure_median_quarters}
          closureP90={summary.closure_p90_quarters}
        />
      )}

      {/* Well Predictions Table */}
      {predictions && (
        <WellPredictionsTable
          predictions={predictions.predictions}
          analytes={predictions.analytes}
          selectedAnalyte={selectedAnalyte}
          onFilterAnalyte={setSelectedAnalyte}
          isLoading={loadingPredictions}
        />
      )}
    </div>
  );
}

function ReceptorsTab({ siteId }: { siteId: string }) {
  const { data: receptors, isLoading } = useQuery({
    queryKey: ['site-receptors', siteId],
    queryFn: () => sitesApi.getReceptors(siteId),
  });

  if (isLoading) {
    return <div className="text-mono-500 text-xs tracking-widest py-8 text-center">LOADING...</div>;
  }

  return (
    <div className="border border-mono-800">
      <div className="px-4 py-3 border-b border-mono-800 bg-mono-900">
        <h3 className="text-[10px] text-mono-500 tracking-widest">RECEPTORS</h3>
      </div>
      <table className="w-full">
        <thead>
          <tr className="border-b border-mono-800">
            <th className="px-4 py-2 text-left text-[10px] text-mono-500 tracking-widest">NAME</th>
            <th className="px-4 py-2 text-left text-[10px] text-mono-500 tracking-widest">TYPE</th>
            <th className="px-4 py-2 text-left text-[10px] text-mono-500 tracking-widest">DISTANCE (FT)</th>
            <th className="px-4 py-2 text-left text-[10px] text-mono-500 tracking-widest">DOWNGRADIENT</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-mono-800">
          {(!receptors || receptors.length === 0) ? (
            <tr>
              <td colSpan={4} className="px-4 py-8 text-center text-mono-600 text-xs tracking-wider">
                NO RECEPTORS
              </td>
            </tr>
          ) : (
            receptors.map((r: any) => (
              <tr key={r.id} className="hover:bg-mono-900/30">
                <td className="px-4 py-2 text-xs text-mono-200">{r.name || '--'}</td>
                <td className="px-4 py-2 text-xs text-mono-400 uppercase">{r.receptor_type}</td>
                <td className="px-4 py-2 text-xs text-mono-400">{r.distance_ft}</td>
                <td className="px-4 py-2 text-xs text-mono-400">
                  {r.is_downgradient == null ? '--' : r.is_downgradient ? 'YES' : 'NO'}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

function StatusPill({ status }: { status: string }) {
  const styles: Record<string, string> = {
    matched: 'bg-green-900/30 text-green-400 border-green-800',
    new: 'bg-blue-900/30 text-blue-400 border-blue-800',
    conflict: 'bg-amber-900/30 text-amber-400 border-amber-800',
  };
  return (
    <span className={`inline-block px-2 py-0.5 text-[10px] tracking-widest border ${styles[status] || 'border-mono-700 text-mono-400'}`}>
      {status.toUpperCase()}
    </span>
  );
}
